/**
 * PaymentScheduleTab — Calendario de pagos a proveedores.
 */
import { Calendar, DollarSign } from 'lucide-react'
import { usePaymentSchedule } from '../hooks/useFinance'
import { useTenantLocale, formatNumber, formatDate } from '@/lib/locale'
import { DataTable } from '@/components/ds/DataTable'
import { EmptyState } from '@/components/EmptyState'

export default function PaymentScheduleTab() {
  const { data, isLoading } = usePaymentSchedule(8)
  const locale = useTenantLocale()
  const fmt = (value: string | number) => {
    const n = typeof value === 'string' ? parseFloat(value) : value
    const decimals = n % 1 !== 0 ? 2 : 0
    return `$${formatNumber(n, locale, decimals)}`
  }

  const schedule = data?.schedule || []

  return (
    <>
      <div className="flex items-center gap-3 mb-4 p-3 bg-glass backdrop-blur-glass border border-divider rounded-card shadow-card">
        <DollarSign size={18} className="text-danger-solid" />
        <span className="text-sm text-content-muted">
          Total por pagar (próximas 8 semanas): <strong className="text-danger-content">{fmt(data?.total || '0')}</strong>
        </span>
        <span className="text-sm text-content-subtle ml-2">{data?.count || 0} facturas</span>
      </div>

      <DataTable
        columns={[
          { key: 'supplier_name', header: 'Proveedor', render: (i: any) => <span className="font-medium text-content">{i.supplier_name}</span> },
          { key: 'invoice_number', header: 'Nro. Factura' },
          {
            key: 'due_date', header: 'Vencimiento',
            render: (i: any) => (
              <span className="flex items-center gap-1.5">
                <Calendar size={14} className="text-content-subtle" /> {formatDate(i.due_date, locale)}
              </span>
            ),
          },
          { key: 'total', header: 'Total', numeric: true, render: (i: any) => <span className="text-content-subtle">{fmt(i.total)}</span> },
          { key: 'outstanding', header: 'Saldo', numeric: true, render: (i: any) => fmt(i.outstanding ?? i.total) },
          {
            key: 'status', header: 'Estado',
            render: (i: any) => (
              <span className="inline-flex px-2 py-0.5 text-xs font-semibold rounded-full bg-warning-surface-strong text-warning-content">
                {i.status_display ?? i.status}
              </span>
            ),
          },
        ]}
        rows={schedule}
        rowKey={(i: any) => i.invoice_id}
        loading={isLoading}
        empty={<EmptyState icon={Calendar} size="sm" title="Sin pagos programados." />}
        minWidth={720}
        caption="Calendario de pagos a proveedores"
      />
    </>
  )
}
